import type { MenuItem } from '../../domain/models/MenuItem';
import type { DesktopNavItem } from '../../domain/models/DesktopNavItem';

/** Parsed shape of /menu/hamburger response (has both menuData and desktopMenuData). */
export interface MenuResponseParsed {
  menuData?: { subData?: unknown[] };
  desktopMenuData?: { subData?: unknown[]; rightAlignedTrailingMenuCount?: unknown };
}

/**
 * Parse /menu/hamburger response once; returns both hamburger and desktop.
 * Same API for both; response has menuData and desktopMenuData.
 */
export function adaptMenuResponseToHamburgerAndDesktop(input: unknown): {
  hamburger: MenuItem[];
  desktop: DesktopNavItem[];
  desktopRightAlignedTrailingMenuCount?: number;
} {
  const record: Record<string, unknown> =
    typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : {};

  if (record.hasError === true) return { hamburger: [], desktop: [] };

  let parsed: MenuResponseParsed | null = null;
  const dataRaw = record.data;

  if (dataRaw != null && typeof dataRaw === 'object') {
    parsed = dataRaw as MenuResponseParsed;
  } else if (typeof dataRaw === 'string') {
    const data = dataRaw.trim();
    if (data && data !== 'undefined' && data !== 'null') {
      try {
        parsed = JSON.parse(data) as MenuResponseParsed;
      } catch (error) {
        console.warn('Error parsing menu response:', error);
        return { hamburger: [], desktop: [] };
      }
    }
  }

  const hamburger = Array.isArray(parsed?.menuData?.subData)
    ? (parsed!.menuData!.subData as unknown as MenuItem[])
    : [];
  const desktop = Array.isArray(parsed?.desktopMenuData?.subData)
    ? (parsed!.desktopMenuData!.subData as unknown as DesktopNavItem[])
    : [];
  const parsedTrailingCount = parsed?.desktopMenuData?.rightAlignedTrailingMenuCount;
  const desktopRightAlignedTrailingMenuCount =
    typeof parsedTrailingCount === 'number' && Number.isFinite(parsedTrailingCount)
      ? Math.max(0, Math.floor(parsedTrailingCount))
      : undefined;

  return { hamburger, desktop, desktopRightAlignedTrailingMenuCount };
}

export function adaptMenuResponseDtoToDomain(input: unknown): MenuItem[] {
  return adaptMenuResponseToHamburgerAndDesktop(input).hamburger;
}

export function adaptDesktopMenuResponseToDomain(input: unknown): DesktopNavItem[] {
  return adaptMenuResponseToHamburgerAndDesktop(input).desktop;
}
